
import React, { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaEye,
} from "react-icons/fa";
import "./ReportsList.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ReportsList({ onBack }) {
  const [miniShot, setMiniShot] = useState(null);
  const [miniShotId, setMiniShotId] = useState(null);

  const [reports, setReports] = useState({
    reality_check: {
      generated: false,
      generating: false,
      score: null,
      reportId: null,
    },
    clarity_check: {
      generated: false,
      generating: false,
      score: null,
      reportId: null,
    },
    fog_index: {
      generated: false,
      generating: false,
      score: null,
      reportId: null,
    },
  });

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Keep loading true until data is fetched

  useEffect(() => {
    const initializeReports = async () => {
      const params = new URLSearchParams(window.location.search);
      const miniShotIdParam = params.get("miniShotId");
      const storedMiniShot = sessionStorage.getItem("selectedMiniShot");

      if (miniShotIdParam) {
        setMiniShotId(miniShotIdParam);
        if (storedMiniShot) {
          setMiniShot(JSON.parse(storedMiniShot));
        }

        // ✅ Wait for reports to load before setting loading to false
        await fetchReportStatuses(miniShotIdParam);
      }

      // ✅ Only set loading to false after data is fetched
      setLoading(false);
    };

    initializeReports();
  }, []);

  const fetchReportStatuses = async (miniShotId) => {
    try {
      const reportTypes = ["reality_check", "clarity_check", "fog_index"];
      const reportStatuses = {
        reality_check: {
          generated: false,
          generating: false,
          score: null,
          reportId: null,
        },
        clarity_check: {
          generated: false,
          generating: false,
          score: null,
          reportId: null,
        },
        fog_index: {
          generated: false,
          generating: false,
          score: null,
          reportId: null,
        },
      };

      // ✅ Use Promise.all to fetch all reports in parallel (faster)
      await Promise.all(
        reportTypes.map(async (reportType) => {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/test-reports?miniShotId=${miniShotId}&reportType=${reportType}&limit=1`,
              {
                credentials: "include",
                headers: { "Content-Type": "application/json" },
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.reports && data.reports.length > 0) {
                const report = data.reports[0];
                reportStatuses[reportType] = {
                  generated: true,
                  generating: false,
                  score:
                    report.overallScore ||
                    report.score ||
                    report.fogIndex ||
                    "N/A",
                  reportId: report.id || report._id,
                };
              }
            }
          } catch (err) {
            console.error(`Error fetching ${reportType}:`, err);
          }
        })
      );

      setReports(reportStatuses);
    } catch (error) {
      console.error("Error fetching report statuses:", error);
      setError("Failed to load report statuses");
    }
  };

  // Add this helper function in your component
  const getReportCircleClass = (report) => {
    // If report not generated, show red (pending state)
    if (!report || !report.generated) {
      return "score-low";
    }

    const score = parseInt(report.score);

    // Handle N/A or invalid scores
    if (isNaN(score)) {
      return "score-low";
    }

    // Score-based color coding
    if (score >= 70) return "score-high";
    if (score >= 40) return "score-medium";
    return "score-low";
  };

  const handleGenerateReport = async (reportType) => {
    setReports((prev) => ({
      ...prev,
      [reportType]: { ...prev[reportType], generating: true },
    }));
    setError(null);

    try {
      console.log(`🚀 Generating ${reportType} for miniShotId: ${miniShotId}`);

      const response = await fetch(
        `${API_BASE_URL}/api/test-reports/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            miniShotId: miniShotId,
            engineType: reportType,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ ${reportType} generation response:`, data);

      if (data.success) {
        await fetchReportStatuses(miniShotId);
        handleViewReport(reportType);
      } else {
        throw new Error(data.error || "Report generation failed");
      }
    } catch (error) {
      console.error("Report generation error:", error);
      setError(`Failed to generate report: ${error.message}`);

      setReports((prev) => ({
        ...prev,
        [reportType]: { ...prev[reportType], generating: false },
      }));
    }
  };

  const handleViewReport = (reportType) => {
    if (miniShot) {
      sessionStorage.setItem("selectedMiniShot", JSON.stringify(miniShot));
      sessionStorage.setItem("skipSelection", "true");
    }

    if (reportType === "reality_check") {
      window.location.href = "/realitycheck?from=chat";
    } else if (reportType === "clarity_check") {
      window.location.href = "/claritycheck?from=chat";
    } else if (reportType === "fog_index") {
      window.location.href = "/fogindex?from=chat";
    }
  };

  const handleBack = () => {
    console.log("🔙 Back button clicked");
    console.log("miniShotId:", miniShotId);

    sessionStorage.removeItem("selectedMiniShot");
    sessionStorage.removeItem("skipSelection");

    if (window.opener && !window.opener.closed) {
      window.opener.focus();
      window.close();
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/mainpage";
    }
  };

  // ✅ Show loading spinner while fetching data
  if (loading) {
    return (
      <div className="reports-list-container">
        <div className="loading-state">
          <FaSpinner className="spin" />
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (!miniShotId) {
    return (
      <div className="reports-list-container">
        <div className="error-state">
          <h2>MiniShot not found</h2>
          <p>Unable to load report data.</p>
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-list-container">
      {/* Header */}
      <div className="reports-header">
        <button onClick={handleBack} className="back-button">
          <FaArrowLeft />
        </button>
        <h1 className="reports-title">
          {miniShot?.title || "Reports"} - Analysis Reports
        </h1>
        <div className="header-spacer"></div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="close-error">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Reports Grid */}
      <div className="reports-grid">
        {/* Reality Check Card */}
        <div className="report-card">
          <div
            className={`report-circle ${getReportCircleClass(
              reports.reality_check
            )}`}
          >
            {reports.reality_check?.generated ? (
              <>
                <div className="score-display">
                  {reports.reality_check.score}
                </div>
              </>
            ) : reports.reality_check?.generating ? (
              <FaSpinner className="spin loading-icon" />
            ) : (
              <FaExclamationTriangle className="pending-icon" />
            )}
          </div>
          <h2 className="report-name">
            {reports.reality_check?.generated
              ? "Reality Check Report"
              : "Reality Check Report - Pending"}
          </h2>
          <p className="report-description">
            Validates your business model and market assumptions. This report
            analyzes whether your core assumptions are realistic and based on
            sound reasoning.
          </p>

          {reports.reality_check?.generated ? (
            <>
              <div className="report-actions">
                <button
                  onClick={() => handleViewReport("reality_check")}
                  className={`view-report-btn ${getReportCircleClass(
                    reports.reality_check
                  )}`}
                >
                  View Report
                </button>
                <button
                  onClick={() => handleGenerateReport("reality_check")}
                  className="regenerate-btn"
                  disabled={reports.reality_check?.generating}
                >
                  {reports.reality_check?.generating ? (
                    <>
                      <FaSpinner className="spin" />
                      Regenerating...
                    </>
                  ) : (
                    "Regenerate"
                  )}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => handleGenerateReport("reality_check")}
              className="generate-report-btn"
              disabled={reports.reality_check?.generating}
            >
              {reports.reality_check?.generating ? (
                <>
                  <FaSpinner className="spin" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          )}
        </div>

        {/* Clarity Check Card */}
        <div className="report-card">
          <div
            className={`report-circle ${getReportCircleClass(
              reports.clarity_check
            )}`}
          >
            {reports.clarity_check?.generated ? (
              <>
                <div className="score-display">
                  {reports.clarity_check.score}
                </div>
              </>
            ) : reports.clarity_check?.generating ? (
              <FaSpinner className="spin loading-icon" />
            ) : (
              <FaExclamationTriangle className="pending-icon" />
            )}
          </div>
          <h2 className="report-name">
            {reports.clarity_check?.generated
              ? "Clarity Check Report"
              : "Clarity Check Report - Pending"}
          </h2>
          <p className="report-description">
            Analyzes the clarity and communication of your idea. This report
            evaluates how well you've articulated your concept and whether it's
            easy to understand.
          </p>

          {reports.clarity_check?.generated ? (
            <>
              <div className="report-actions">
                <button
                  onClick={() => handleViewReport("clarity_check")}
                  className={`view-report-btn ${getReportCircleClass(
                    reports.clarity_check
                  )}`}
                >
                  View Report
                </button>
                <button
                  onClick={() => handleGenerateReport("clarity_check")}
                  className="regenerate-btn"
                  disabled={reports.clarity_check?.generating}
                >
                  {reports.clarity_check?.generating ? (
                    <>
                      <FaSpinner className="spin" />
                      Regenerating...
                    </>
                  ) : (
                    "Regenerate"
                  )}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => handleGenerateReport("clarity_check")}
              className="generate-report-btn"
              disabled={reports.clarity_check?.generating}
            >
              {reports.clarity_check?.generating ? (
                <>
                  <FaSpinner className="spin" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          )}
        </div>

        {/* Fog Index Card */}
        <div className="report-card">
          <div
            className={`report-circle ${getReportCircleClass(
              reports.fog_index
            )}`}
          >
            {reports.fog_index?.generated ? (
              <>
                <div className="score-display">{reports.fog_index.score}</div>
              </>
            ) : reports.fog_index?.generating ? (
              <FaSpinner className="spin loading-icon" />
            ) : (
              <FaExclamationTriangle className="pending-icon" />
            )}
          </div>
          <h2 className="report-name">
            {reports.fog_index?.generated
              ? "Fog Index Report"
              : "Fog Index Report - Pending"}
          </h2>
          <p className="report-description">
            Measures the readability and complexity of your concept. A lower
            score indicates better readability and simpler language.
          </p>

          {reports.fog_index?.generated ? (
            <>
              <div className="report-actions">
                <button
                  onClick={() => handleViewReport("fog_index")}
                  className={`view-report-btn ${getReportCircleClass(
                    reports.fog_index
                  )}`}
                >
                  View Report
                </button>
                <button
                  onClick={() => handleGenerateReport("fog_index")}
                  className="regenerate-btn"
                  disabled={reports.fog_index?.generating}
                >
                  {reports.fog_index?.generating ? (
                    <>
                      <FaSpinner className="spin" />
                      Regenerating...
                    </>
                  ) : (
                    "Regenerate"
                  )}
                </button>
              </div>
            </>
          ) : (
            <button
              onClick={() => handleGenerateReport("fog_index")}
              className="generate-report-btn"
              disabled={reports.fog_index?.generating}
            >
              {reports.fog_index?.generating ? (
                <>
                  <FaSpinner className="spin" />
                  Generating...
                </>
              ) : (
                "Generate Report"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
